import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { appBus } from '@glu/core';
import { withStyles } from '@glu/theming';
import { ArrowLeftAshIcon } from '@glu/icons-react';
import history from 'system/history';
import util from 'system/util';
import DangerouslySetInnerHTML from 'system/components/DangerouslySetInnerHTML';
import locale from '@glu/locale';
import routeManager from '@glu/core/src/routeManager';

const styles = ({
  pageHeadline,
  breakpoints,
  gluContainer,
  pageTitle,
  subHeading,
  theme,
  pageTitleContainer,
  noPrint,
  spacing: { pageMargins }
}) => ({
  pageHeadline,
  pageTitle: theme === 'legacy' ? {
    padding: '0 !important',
    display: 'flex',
    alignItems: 'center'
  } : {
    display: 'flex',
    height: 104,
    alignItems: 'center',
    marginBottom: 0,
    width: '100%',
    lineHeight: 'unset'
  },
  pageBack: theme === 'legacy' ? {
    color: '#fff',
    margin: 0,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    height: 49,
    lineHeight: '49px',
    width: 45,
    backgroundColor: '#333',
    textAlign: 'center'
  } : {
    marginRight: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      background: '#f4f7fa',
      textDecoration: 'none'
    },
    color: '#0070b9 !important',
    width: 40,
    height: 40,
    borderRadius: 40
  },

  iconArrow: theme === 'legacy' ? {
    margin: [0, 'auto'],
    fontSize: 16,
    '& > svg': {
      fill: '#fff'
    }
  } : {
    '& > svg': {
      fill: '#0070b9',
      width: 23,
      height: 22,
      fontSize: 20
    }
  },

  h1: theme === 'legacy' ? {
    color: '#fff',
    fontWeight: 300,
    lineHeight: '49px',
    height: '100%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    margin: [0, 20]
  } : {
    ...pageTitle,
    flexGrow: 1,
    marginBottom: 0,
    lineHeight: '48px',
    height: 40,
    display: 'flex',
    alignItems: 'flex-end',
    fontSize: 40
  },

  aside: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1
  },

  colorbar: {
    boxShadow: '0 2px 2px 0 #999',
    zIndex: 1,
    width: '100%',
    height: 8,
    display: 'flex',
    '&:before, &:after': {
      content: '" "', // 1
      display: 'table' // 2
    },
    '&:after': {
      clear: 'both'
    }
  },
  ...(() => (
    [
      '#693',
      '#3e7d65',
      '#37939b',
      '#4da9a4',
      '#5491b1',
      '#6fabcd',
      '#80a1b6'
    ].reduce((colbars, backgroundColor, index) => ({
      ...colbars,
      [`colbar${index + 1}`]: {
        backgroundColor,
        flexGrow: 1,
        height: 8
      }
    }), {})
  ))(),
  container: {
    ...gluContainer,
    ...pageTitleContainer,
    ...noPrint,
    padding: 0,
    width: 'auto !important',
    borderBottom: '1px solid #EDEDED',
    margin: 0,
    maxWidth: 'unset',

    '& header': {
      ...pageMargins
    },
  },
  h2: {
    ...subHeading,
    marginBottom: 26
  },
  header: {
    display: 'flex',
    flexGrow: 1,
    margin: 0
  },
  rightContent: {
    /* zIndex: 2, */
    paddingLeft: 20,

    [`@media screen and (max-width: ${breakpoints.tablet - 1}px)`]: {
      display: 'block',
      paddingLeft: 0
    }
  }
});

export default @withStyles(styles) class PageHeadline extends Component {
  static Title = ({ title, containHTML }) => {
    if (containHTML) {
      return DangerouslySetInnerHTML({
        element: 'span',
        html: title === 'function' ? title() : title
      });
    }
    return (
      <span>{title === 'function' ? title() : title}</span>
    );
  };

  static updateWidth() {
    const $mainContent = document.querySelector('[data-qa="main-content"]');
    const $pageHeadline = document.querySelector('[data-qa="page-title"]');
    if (!$mainContent) {
      return;
    }
    if ($pageHeadline) {
      $pageHeadline.style.width = `${$mainContent.offsetWidth}px`;
    }
  }

  static propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    view: PropTypes.objectOf(PropTypes.any),
    options: PropTypes.shape({}),
    title: PropTypes.string.isRequired,
    properties: PropTypes.arrayOf(PropTypes.string),
    useAppBusEvents: PropTypes.bool,
    /** Additional content to be displayed on the far right inline with the heading. */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    subtitle: PropTypes.node
  };

  static defaultProps = {
    view: {},
    options: {},
    properties: [],
    useAppBusEvents: true,
    children: null,
    subtitle: null
  };

  constructor(props) {
    super(props);

    const {
      view, title, options, properties
    } = this.props;

    this.state = {
      options,
      view,
      title,
      ...properties.reduce((memo, item) => ({ ...memo, [item]: null }), {})
    };
  }

  componentDidMount() {
    const { title } =this.props;
    this.addListeners();

    util.generateTitle(title === 'function' ? title() : title);
  }

  componentDidUpdate(prevProps) {
    const { useAppBusEvents } = this.props;

    if (useAppBusEvents === prevProps.useAppBusEvents) {
      return;
    }

    this.removeListeners();
    this.addListeners();
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  static getDerivedStateFromProps(props, state) {
    const { title } = props;

    if (title !== state.title) {
      return {
        ...state,
        title
      };
    }
    return null;
  }

  addListeners = () => {
    const { view, properties, useAppBusEvents } = this.props;
    if (!useAppBusEvents) { return; }
    appBus.listenTo(appBus, 'show:subPage', this.showCustomPageTitle);
    properties.forEach((prop) => {
      // eslint-disable-next-line no-unused-expressions
      view.model?.listenTo(view.model, `change:${prop}`, () => this.setState({ [prop]: view.model.get(prop) }));
    });
    this.timer = setInterval(PageHeadline.updateWidth, 150);
  };

  removeListeners = () => {
    const { view, properties, useAppBusEvents } = this.props;
    if (!useAppBusEvents) { return; }
    appBus.stopListening(appBus, 'show:subPage', this.showCustomPageTitle);
    properties.forEach((prop) => {
      // eslint-disable-next-line no-unused-expressions
      view.model?.stopListening(view.model, `change:${prop}`, () => this.setState({ [prop]: view.model.get(prop) }));
    });
    if (this.timer) {
      clearInterval(this.timer);
    }
  };

  showCustomPageTitle = (title, passedOptions, containHTML) => {
    this.setState((prevState) => ({
      options: {
        ...prevState.options,
        passedOptions
      },
      title: containHTML ? title() : title,
      containHTML
    }));
  };

  onGoBackClick = (e) => {
    e.preventDefault();
    const { options } = this.state;

    if (!options.backUrl) {
      return window.history.go(-1);
    }

    const { name, params, query } = options.backUrl;

    return history().push(`/${routeManager[name].params(params).query(query)}`);
  };

  render() {
    const {
      title, options, view, containHTML
    } = this.state;

    if (!view || !options) {
      return null;
    }

    const {
      classes, properties, children, subtitle
    } = this.props;

    const propertiesValues = properties.reduce((memo, prop) => (
      // eslint-disable-next-line react/destructuring-assignment
      this.state[prop] ? [...memo, this.state[prop]] : memo
    ), []);
    const hasValues = propertiesValues.length;
    const { backButton, backUrl = '#' } = options;

    if (util.isGluAsh()) {
      return (
        <section
          className={classes.container}
          data-qa="page-title"
        >
          <header className={classes.pageTitle}>
            {backButton && (
              <a
                className={classes.pageBack}
                href={routeManager[backUrl.name]}
                title={locale.get('back')}
                onClick={this.onGoBackClick}
                data-qa="headline-back-button"
              >
                <ArrowLeftAshIcon className={classes.iconArrow} />
              </a>
            )}
            <h1
              className={classes.h1}
              data-qa="page-title-heading"
            >
              {properties.length && hasValues ? (
                propertiesValues.map((propVal) => (
                  <Fragment key={`property-${propVal}`}>
                    <span>{propVal}</span>
                    {' '}
                  </Fragment>
                ))
              ) : (
                <PageHeadline.Title title={title} containHTML={containHTML} />
              )}
            </h1>
            {children && (
              <div className={classes.rightContent}>
                {children}
              </div>
            )}
          </header>
          {subtitle && (
            <h2
              className={classes.h2}
              data-qa="page-title-subtitle"
            >
              {subtitle}
            </h2>
          )}
        </section>
      );
    }

    return (
      <section className={classes.pageHeadline} data-qa="page-title">
        <div className={classes.pageTitle}>
          {backButton && (
            <a
              className={classes.pageBack}
              href={`/${ routeManager[backUrl.name]}`}
              title={locale.get('back')}
              onClick={this.onGoBackClick}
              data-qa="headline-back-button"
            >
              <ArrowLeftAshIcon className={classes.iconArrow} />
            </a>
          )}
          <h1 className={classes.h1}>
            {properties.length && hasValues ? (
              propertiesValues.map((propVal) => (
                <Fragment key={`property-${propVal}`}>
                  <span>{propVal}</span>
                  {' '}
                </Fragment>
              ))
            ) : (
              <PageHeadline.Title title={title} containHTML={containHTML} />
            )}
          </h1>
          {children && (
            <div className={classes.aside}>
              {children}
            </div>
          )}
        </div>
        <div className={classes.colorbar}>
          {[...Array(6)].map((value, index) => (
            <div
              key={`colbar-${index + 1}`}
              className={classes[`colbar${index + 1}`]}
            />
          ))}
        </div>
      </section>
    );
  }
}
